import type { FeeCharge, FeeDiscount } from './feeBreakdown';

/** The student fields a receipt renders. */
export interface ReceiptStudent {
    studentName: string;
    studentEnrollment?: string;
    studentClass?: string;
    fatherName?: string;
    motherName?: string;
    contactNumber?: string;
    address?: string;
    photoUrl?: string;
}

export interface ReceiptData extends ReceiptStudent {
    schoolName?: string;
    /** Printed under the school name in the header. Omitted when the school has none. */
    schoolAddress?: string;
    invoiceNumber: string;
    /** The invoice's billing date — the receipt shows the month a fee is *for*.
        Falls back to the payment date when the caller doesn't know it. */
    billingDate?: string;
    /** The period this payment covers, when it isn't a single month ("May–Jul 2026").
        Overrides the month derived from `billingDate`. */
    periodLabel?: string;
    /** Charges at their gross (pre-discount) amounts. */
    items?: FeeCharge[];
    /** Discounts, grouped after the charges. Positive amounts; rendered as deductions. */
    discounts?: FeeDiscount[];
    /** Sum of the gross charges. Only shown when there are discounts to deduct. */
    subtotal?: number;
    /** Net payable: subtotal − discounts. */
    totalAmount?: number;
    amountPaid: number;
    /** What the invoice still owes after this payment. Printed in red when it's > 0.
        Comes from the server's invoice, not `totalAmount − amountPaid` — that would
        ignore anything the student had already paid on the same invoice. */
    balanceDue?: number;
    /** Money received beyond what was owed, held against the student rather than any
        invoice. Printed as an advance so an overpayment is on the paper, not silent. */
    advanceAmount?: number;
    paymentMethod: string;
    referenceNumber: string;
    collectedAt: string;
}

/** The student as embedded in a fee-transaction record. */
export interface ReceiptStudentSource {
    name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    enrollment_number?: string | null;
    father_name?: string | null;
    mother_name?: string | null;
    father_contact_number?: string | null;
    address?: string | null;
    profile_photo_url?: string | null;
    class?: { name: string } | null;
    section?: { name: string } | null;
}

const clean = (v: string | null | undefined): string | undefined => {
    const s = (v ?? '').trim();
    return s.length > 0 ? s : undefined;
};

/** Map a fee-transaction's `student` onto the fields the receipt renders. */
export function toReceiptStudent(
    s: ReceiptStudentSource | null | undefined,
    fallbackName?: string,
): ReceiptStudent {
    const fullName = clean(s?.name) ?? clean([s?.first_name, s?.last_name].filter(Boolean).join(' '));
    const className = clean(s?.class?.name);
    const sectionName = clean(s?.section?.name);
    return {
        studentName: fullName ?? clean(fallbackName) ?? '—',
        studentEnrollment: clean(s?.enrollment_number),
        studentClass: className && sectionName ? `${className} · ${sectionName}` : className,
        fatherName: clean(s?.father_name),
        motherName: clean(s?.mother_name),
        contactNumber: clean(s?.father_contact_number),
        address: clean(s?.address),
        photoUrl: clean(s?.profile_photo_url),
    };
}

/** School crest printed at the top of every receipt copy. */
const RECEIPT_LOGO_URL = 'https://api.gurukulsync.in/storage/ktic-logo.jpeg';

/** Printed under the school's name when the school record carries no address of its own —
    so the paper always says where the money was handed over. A real address from the API
    wins over this. Worded to match the seal, which names the same place. */
const RECEIPT_ADDRESS = 'Surajkunda, Raebareli';

function formatCurrency(n: number): string {
    return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

/** "July, 2026". A date-only string (an invoice's issue date) is read as a calendar
    date, so the month can't slip backwards in a timezone behind UTC. */
function formatMonth(iso: string): string {
    const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    const d = ymd
        ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
        : new Date(iso);
    return `${MONTH_NAMES[d.getMonth()]}, ${d.getFullYear()}`;
}

/** Receipt values are interpolated into markup, so they must be escaped. */
function esc(v: string | number | null | undefined): string {
    return String(v ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/** Open the receipt window up-front, inside the click that asked for it — a window
    opened later (after an await) is treated as an unsolicited popup and blocked.
    Hand the returned window to `printReceipt` once the data is in hand. */
export function openReceiptWindow(): Window | null {
    const w = window.open('', '_blank', 'width=900,height=900');
    if (w) {
        w.document.write(
            '<!DOCTYPE html><title>Receipt</title>'
            + '<body style="font:14px system-ui,sans-serif;color:#64748b;padding:24px">Preparing receipt…</body>'
        );
    }
    return w;
}

export function printReceipt(data: ReceiptData, target?: Window | null) {
    const methodLabel = data.paymentMethod.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    const monthStr = data.periodLabel || formatMonth(data.billingDate || data.collectedAt);
    const dateStr = formatDate(data.collectedAt);
    // en-IN renders the meridiem lowercase ("10:35 am"); the receipt wants "10:35 AM".
    const timeStr = new Date(data.collectedAt)
        .toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
        .toUpperCase();

    // The <img> keeps its box on failure and reveals the fallback underneath, so a
    // broken/unreachable photo URL still prints a well-formed receipt.
    const photoHtml = data.photoUrl
        ? `<div class="photo">
             <span class="photo__fallback">Photo missing</span>
             <img src="${esc(data.photoUrl)}" alt="" onerror="this.style.display='none'">
           </div>`
        : `<div class="photo"><span class="photo__fallback">Photo missing</span></div>`;

    const idLine = [data.studentClass, data.studentEnrollment].filter(Boolean).map(esc).join(' &middot; ');

    const parentRows = [
        data.fatherName ? `<div class="row"><span class="label">Father</span><span class="value">${esc(data.fatherName)}</span></div>` : '',
        data.motherName ? `<div class="row"><span class="label">Mother</span><span class="value">${esc(data.motherName)}</span></div>` : '',
        data.address ? `<div class="row"><span class="label">Address</span><span class="value">${esc(data.address)}</span></div>` : '',
    ].join('');

    // Charges at gross, then discounts grouped beneath — a Subtotal row bridges the
    // two so the deduction is auditable. With no discounts it collapses to charges + Total.
    const discounts = data.discounts ?? [];
    const subtotalHtml = discounts.length > 0
        ? `<tr class="subtotal"><td>Subtotal</td><td class="amt">${formatCurrency(data.subtotal ?? 0)}</td></tr>
           <tr class="group"><td colspan="2">Discounts</td></tr>
           ${discounts.map((d) => `<tr class="discount"><td>${esc(d.name)}</td><td class="amt">− ${formatCurrency(d.amount)}</td></tr>`).join('')}`
        : '';

    // Rows are rendered at full size; fitItems() in the print window tightens them only if
    // they don't fit the card. Guessing from a row count (as this once did) gets it wrong —
    // the space a table needs depends on the header, the address and the seal above it too.
    const lineItemsHtml = data.items && data.items.length > 0
        ? `<table class="items">
            <thead><tr><th>Description</th><th class="amt">Amount</th></tr></thead>
            <tbody>
                ${data.items.map((item) => `<tr><td>${esc(item.name)}</td><td class="amt">${formatCurrency(item.amount)}</td></tr>`).join('')}
                ${subtotalHtml}
                <tr class="total"><td><strong>Total</strong></td><td class="amt"><strong>${formatCurrency(data.totalAmount ?? 0)}</strong></td></tr>
            </tbody>
          </table>`
        : '';

    // Sub-rupee residue from rounding isn't a balance anybody should chase.
    const balanceDue = data.balanceDue !== undefined && data.balanceDue > 0.5 ? data.balanceDue : 0;
    const advance = data.advanceAmount !== undefined && data.advanceAmount > 0.5 ? data.advanceAmount : 0;

    const refHtml = data.referenceNumber
        ? `<div class="row"><span class="label">Reference No.</span><span class="value num">${esc(data.referenceNumber)}</span></div>`
        : '';

    const renderReceipt = () => `
    <div class="receipt">
      <div class="header">
        <img class="header__logo" src="${RECEIPT_LOGO_URL}" alt="" onerror="this.style.display='none'">
        <h1 class="header__name">${esc(data.schoolName || 'Gurukul Academy')}</h1>
        <p class="header__addr">${esc(data.schoolAddress || RECEIPT_ADDRESS)}</p>
        <p class="header__tag">Fee Payment Receipt</p>
      </div>

      <div class="student">
        ${photoHtml}
        <div class="student__info">
          <div class="student__name">${esc(data.studentName)}</div>
          ${idLine ? `<div class="student__id">${idLine}</div>` : ''}
          ${parentRows}
        </div>
      </div>

      <div class="meta">
        <div class="row"><span class="label">Invoice No.</span><span class="value num">${esc(data.invoiceNumber)}</span></div>
      </div>

      <div class="items-wrap">${lineItemsHtml}</div>

      <div class="paid-block">
        <div class="highlight${balanceDue > 0 || advance > 0 ? ' highlight--part' : ''}">
          <div class="amount">${formatCurrency(data.amountPaid)}</div>
          <div class="period">Fee for ${esc(monthStr)}</div>
          <div class="sub">Received via ${esc(methodLabel)} &middot; ${esc(dateStr)} at ${esc(timeStr)}</div>
          <span class="badge">PAID</span>
          ${balanceDue > 0
            ? `<div class="balance"><span class="balance__label">Balance Due</span><span class="balance__amt">${formatCurrency(balanceDue)}</span></div>`
            : ''}
          ${advance > 0
            ? `<div class="advance"><span class="advance__label">Advance</span><span class="advance__amt">${formatCurrency(advance)}</span></div>`
            : ''}
        </div>
      </div>

      ${refHtml ? `<div class="meta">${refHtml}</div>` : ''}

      <div class="footer">
        <p>Thank you for your payment!</p>
        <p>Computer-generated receipt &middot; No signature required.</p>
      </div>
    </div>`;

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Receipt — ${esc(data.invoiceNumber)}</title>
<style>
  @page { size: A4 portrait; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  html, body { width: 210mm; height: 297mm; }
  body {
    font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
    color: #0f172a;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .page { width: 210mm; height: 297mm; position: relative; page-break-after: avoid; }
  .top-half {
    width: 210mm;
    height: 148.5mm;
    display: grid;
    grid-template-columns: 105mm 105mm;
    border-bottom: 1px dashed #cbd5e1;
  }
  /* Fixed half-page card. The items block is the only flexible row, so the
     amount and footer stay pinned no matter how many lines an invoice has. */
  .receipt {
    width: 105mm;
    height: 148.5mm;
    padding: 5mm 6mm;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-right: 1px dashed #cbd5e1;
  }
  .receipt:nth-child(2n) { border-right: none; }

  /* Balanced, centred header: crest on top, then the name, address and caption stacked and
     centred beneath it. The school's name is the biggest thing on the paper — fitName() grows
     it to the largest size that still fits the card. */
  .header {
    padding-bottom: 6px;
    border-bottom: 2px solid #0f172a;
    text-align: center;
  }
  /* A fixed box, centred above the name. Its height is reserved whether or not the image
     loads, so the name below can't jump as a slow or broken crest resolves. */
  .header__logo {
    display: block;
    margin: 0 auto 3px;
    width: 18mm;
    height: 12mm;
    object-fit: contain;
  }
  /* Sized by fitName(), which is why no font-size is set here — anything set would only be
     the size it starts from. Two lines is the cap: the header sits above a fixed items area,
     so the name may not eat the rows the invoice needs. */
  .header__name {
    font-weight: 800;
    letter-spacing: -0.025em;
    line-height: 1.06;
    overflow-wrap: anywhere;
  }
  .header__addr {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: 7.5px;
    color: #475569;
    line-height: 1.35;
    margin-top: 2px;
  }
  /* Centred caption naming what the paper is. */
  .header__tag {
    margin-top: 5px;
    font-size: 8px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: #64748b;
  }
  .student { display: flex; gap: 7px; padding: 7px 0; }
  .photo {
    position: relative;
    flex: none;
    width: 17mm;
    height: 21mm;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    background: #f8fafc;
    overflow: hidden;
  }
  .photo img {
    position: relative;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  /* Sits beneath the photo; shows through when the image is absent or fails. */
  .photo__fallback {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0 2px;
    font-size: 7px;
    line-height: 1.3;
    color: #94a3b8;
  }
  .student__info { flex: 1; min-width: 0; }
  .student__name { font-size: 12px; font-weight: 700; letter-spacing: -0.01em; line-height: 1.25; }
  .student__id {
    font-size: 8px;
    font-weight: 600;
    color: #64748b;
    margin: 1px 0 4px;
    padding-bottom: 4px;
    border-bottom: 1px solid #f1f5f9;
  }

  .row { display: flex; justify-content: space-between; gap: 8px; font-size: 9px; padding: 1.5px 0; }
  .label { flex: none; color: #64748b; }
  .value { font-weight: 600; text-align: right; min-width: 0; overflow-wrap: anywhere; }
  .num { font-variant-numeric: tabular-nums; }

  .meta { padding: 5px 0; border-top: 1px solid #f1f5f9; }

  .items-wrap { flex: 1; min-height: 0; overflow: hidden; padding: 5px 0; border-top: 1px solid #f1f5f9; }
  .items { width: 100%; border-collapse: collapse; font-size: 9px; }
  .items th {
    text-align: left;
    font-size: 7px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #64748b;
    padding: 0 0 3px;
    border-bottom: 1px solid #e2e8f0;
  }
  .items td { padding: 2.5px 0; border-bottom: 1px solid #f8fafc; }
  .items .amt { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .items th.amt { text-align: right; }
  .items .subtotal td { border-top: 1px solid #e2e8f0; border-bottom: none; padding-top: 3px; color: #475569; }
  .items .group td {
    padding: 4px 0 1px;
    border-bottom: none;
    font-size: 7px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #64748b;
  }
  .items .discount td { color: #b45309; border-bottom: 1px solid #f8fafc; }
  .items .total td { border-top: 1px solid #0f172a; border-bottom: none; padding-top: 3px; }

  /* Long invoice: same rows, tighter. Sized so the worst case the card can hold — a
     two-line school address, eight charges, discounts and a reference row — still prints
     its Total rather than clipping it off the page. */
  .items-wrap--compact { padding: 2px 0; }
  /* Whitespace only — type size is the fitter's next lever, not this one. */
  .items--compact td { padding: 0; line-height: 1.2; }
  .items--compact th { padding-bottom: 1.5px; }
  .items--compact .group td { padding: 1px 0 0; }
  .items--compact .subtotal td, .items--compact .total td { padding-top: 1.5px; }

  .highlight {
    position: relative;
    text-align: center;
    padding: 7px 6px;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 5px;
  }
  .highlight .amount { font-size: 18px; font-weight: 800; color: #059669; font-variant-numeric: tabular-nums; line-height: 1.15; }
  .highlight .period { font-size: 8.5px; font-weight: 700; color: #047857; line-height: 1.3; margin-top: 1px; }
  /* Method, date and time on one line — kept nowrap-free so a long method label wraps
     rather than overflowing the box. */
  .highlight .sub { font-size: 8px; color: #64748b; margin-top: 1px; line-height: 1.35; }
  .highlight .badge {
    position: absolute;
    top: 5px;
    right: 6px;
    font-size: 7px;
    font-weight: 800;
    letter-spacing: 0.08em;
    color: #059669;
    border: 1px solid #86efac;
    border-radius: 100px;
    padding: 1px 5px;
  }

  /* Part payment: what was received stays green, what is still owed is called out in red
     directly under it, inside the same box as the PAID badge so the two can't be read apart. */
  .highlight--part { padding-bottom: 5px; }
  .highlight .balance {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    margin-top: 5px;
    padding-top: 4px;
    border-top: 1px dashed #fca5a5;
  }
  .highlight .balance__label {
    font-size: 7.5px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #dc2626;
  }
  .highlight .balance__amt {
    font-size: 10px;
    font-weight: 800;
    color: #dc2626;
    font-variant-numeric: tabular-nums;
  }

  /* Overpayment: money taken in that no invoice claimed. Called out in blue — it is
     neither a shortfall nor part of what was billed, and the payer is owed it back
     as credit against a later month. */
  .highlight .advance {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    margin-top: 5px;
    padding-top: 4px;
    border-top: 1px dashed #93c5fd;
  }
  .highlight .advance__label {
    font-size: 7.5px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #1d4ed8;
  }
  .highlight .advance__amt {
    font-size: 10px;
    font-weight: 800;
    color: #1d4ed8;
    font-variant-numeric: tabular-nums;
  }

  .footer { text-align: center; padding-top: 5px; margin-top: 5px; border-top: 1px dashed #cbd5e1; }
  .footer p { font-size: 7px; color: #94a3b8; line-height: 1.5; }
  .footer p:first-child { font-weight: 600; color: #64748b; }
</style>
</head>
<body>
  <div class="page">
    <div class="top-half">
      ${renderReceipt()}
      ${renderReceipt()}
    </div>
  </div>
<script>
  // The school's name is set as large as its column can hold: start at the biggest size the
  // card could plausibly carry and come down until it fits two lines. Measured rather than
  // hard-coded because the name is the school's, not ours — "KTIC" and "Karpoori Thakur
  // Inter College" cannot both be right at one size.
  function fitName() {
    var names = document.querySelectorAll('.header__name');
    var MAX = 40, MIN = 11;
    for (var i = 0; i < names.length; i++) {
      var el = names[i];
      var size = MAX;
      el.style.fontSize = size + 'px';
      // Count the lines rather than compare heights: a two-line block measures a few px
      // taller than twice its line-height (half-leading, rounding), and testing that way
      // rejects a name that in fact fits — shrinking it for nothing.
      var fits = function () {
        var lh = parseFloat(getComputedStyle(el).lineHeight) || size * 1.06;
        var lines = Math.round(el.scrollHeight / lh);
        return lines <= 2 && el.scrollWidth <= el.clientWidth + 1;
      };
      var guard = 0;
      while (!fits() && size > MIN && guard++ < 60) {
        size -= 0.5;
        el.style.fontSize = size + 'px';
      }
    }
  }

  // The card is a fixed half-page, so a long invoice would push its Total off the paper.
  // Shrink the items table until it fits: a smaller line beats a missing one.
  function fitItems() {
    var cards = document.querySelectorAll('.receipt');
    for (var i = 0; i < cards.length; i++) {
      var wrap = cards[i].querySelector('.items-wrap');
      var table = wrap && wrap.querySelector('.items');
      if (!table) continue;
      var cs = getComputedStyle(wrap);
      var room = wrap.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);

      var over = function () { return table.getBoundingClientRect().height > room; };
      var guard = 0;

      // Row padding first: it is pure whitespace, so losing it costs no legibility at all.
      if (over()) {
        table.classList.add('items--compact');
        wrap.classList.add('items-wrap--compact');
      }

      // Then type size. The floor is low because a small line still reads; a line hidden
      // under the seal, or pushed off the card, does not.
      var size = parseFloat(getComputedStyle(table).fontSize);
      while (over() && size > 4.75 && guard++ < 40) {
        size -= 0.25;
        table.style.fontSize = size + 'px';
      }
      // Then close the leading, for the rare invoice that is still too long. This has to
      // land on the cells: .items--compact sets td line-height, which would out-specify
      // anything inherited from the table.
      var cells = table.querySelectorAll('td, th');
      var lead = 1.2;
      while (over() && lead > 0.95 && guard++ < 80) {
        lead -= 0.05;
        for (var c = 0; c < cells.length; c++) cells[c].style.lineHeight = String(lead);
      }
    }
  }

  // Print once the photo has settled, but never wait on a hanging image host.
  (function () {
    var printed = false;
    // Name first: it decides how much height is left for the items to fit into.
    function fit() { fitName(); fitItems(); }
    function go() { if (printed) return; printed = true; fit(); window.print(); }
    /* Fit now, not only on load: this document was written into a window that was already
       open, where the load event may never fire again — and a receipt whose rows are still
       spilling off the card is wrong on screen, not just on paper. */
    fit();
    window.onload = go;
    setTimeout(go, 2500);
  })();
<${'/'}script>
</body>
</html>`;

    const w = target ?? window.open('', '_blank', 'width=900,height=900');
    if (w) {
        // Reset first: `target` already holds the placeholder markup.
        w.document.open();
        w.document.write(html);
        w.document.close();
    }
}
